
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    DollarSign, FileText, Upload, Printer, Trash2,
    Ship, MapPin, Package, Calendar, User, Filter,
    ChevronRight, Eye, Clock, CheckCircle2, AlertCircle,
    ArrowDownZA, ArrowUpAZ, Mail,
    TrendingUp, Sparkles, ArrowUpRight, ArrowDownRight,
    Globe, XCircle, Brain, RotateCcw, Plus,
    List, LayoutGrid, BookmarkPlus, Activity,
} from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    getRateQuoteList, deleteRateQuote,
    getCancelledRateQuoteList, reinstateRateQuoteList,
    getRateQuoteByID, createRateQuote,
} from '@/redux/RateQuote/actions';
import moment from 'moment';
import {
    DropdownMenu, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuPortal, DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import SearchInputWithClear from '@/components/ui/SearchInputWithClear';
import FileUpload from '@/components/FileUpload/FileUpload';
import RateQuoteNote from './RateQuote/RateQuoteNote';
import RateQuoteCancelModal from '@/components/Modal/RateQuoteCancelModal';
import RateQuoteCancelRestoreModal from '@/components/Modal/RateQuoteCancelRestoreModal';
import { showToast } from '@/redux/Toast/actions';
import PortBadge from './RateQuote/PortBadge';
import AIInsightCard from '@/components/shipment/AIInsightCard';
import QuoteCard from '@/components/RateQuote/quoteCard';
import { callLoader } from '@/redux/Loader/actions';
import { error } from 'ajv/dist/vocabularies/applicator/dependencies';

// ─── Animated typewriter title ────────────────────────────────────────────────
const TITLE = 'Rate Quote Management';
function AnimatedTitle() {
    return (
        <motion.h1
            initial={{ opacity: 0, y: 8, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                 text-gray-900 dark:text-white"
        >
            <motion.span
                className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                initial={{ width: '0ch' }}
                animate={{ width: ['0ch', `${TITLE.length}ch`, `${TITLE.length}ch`, '0ch'] }}
                transition={{
                    duration: 4.8,
                    times: [0, 0.22, 0.9, 1],
                    ease: ['linear', 'linear', 'easeInOut'],
                    repeat: Infinity,
                }}
            >
                {TITLE}
            </motion.span>
        </motion.h1>
    );
}

// ─── View Toggle ──────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
    const base = 'w-9 h-9 flex items-center justify-center rounded-md transition-colors';
    const active = 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
    const inactive = 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100';

    return (
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                    bg-white dark:bg-gray-900 p-0.5">
            <button onClick={() => setView('table')} title="List view"
                className={`${base} ${view === 'table' ? active : inactive}`}>
                <List className="w-4 h-4" />
            </button>
            <button onClick={() => setView('card')} title="Grid view"
                className={`${base} ${view === 'card' ? active : inactive}`}>
                <LayoutGrid className="w-4 h-4" />
            </button>
        </div>
    );
}

// ─── AI Insights Modal ────────────────────────────────────────────────────────
// useTranslation is called inside the component (fix from v1 where `t` was used from outer scope)
const AIInsightsModal = ({ onClose = () => { }, insightCards = [] }) => {
    const { t } = useTranslation(); // ← conflict fix: must be inside component

    const statThemes = [
        { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', sub: 'text-blue-600/70 dark:text-blue-400/70' },
        { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', sub: 'text-emerald-600/70 dark:text-emerald-400/70' },
        { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400', sub: 'text-red-600/70 dark:text-red-400/70' },
        { bg: 'bg-purple-50 dark:bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', sub: 'text-purple-600/70 dark:text-purple-400/70' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl
                   w-full max-w-3xl max-h-[80vh] overflow-hidden"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">{t('aiQuoteAnalytics')}</h2>
                                <p className="text-violet-100 text-sm">{t('aiQuoteAnalyticsDescription')}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                            <XCircle className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* Summary stats */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        {insightCards.map((card, i) => (
                            <div key={card.title} className={`${statThemes[i]?.bg} rounded-xl p-4 text-center`}>
                                <p className={`text-2xl font-bold ${statThemes[i]?.text}`}>{card.value}</p>
                                <p className={`text-xs font-medium ${statThemes[i]?.sub}`}>{card.title}</p>
                            </div>
                        ))}
                    </div>

                    {/* AI Predictions */}
                    <div className="bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                            <h3 className="font-bold text-slate-800 dark:text-white">{t('aiPredictions')}</h3>
                        </div>
                        <div className="space-y-3">
                            {[
                                { icon: CheckCircle2, color: 'bg-emerald-500', title: t('highConversionPotential'), desc: t('highConversionPotentialDescription') },
                                { icon: AlertCircle, color: 'bg-amber-500', title: t('expiringSoon'), desc: t('expiringSoonDescription') },
                                { icon: TrendingUp, color: 'bg-blue-500', title: t('marketTrend'), desc: t('marketTrendDescription') },
                            ].map(({ icon: Icon, color, title, desc }) => (
                                <div key={title} className="flex items-start gap-3">
                                    <div className={`w-6 h-6 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                        <Icon className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{title}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top Routes & Customers */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">{t('topRoutes')}</h3>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { from: 'SAVANNAH, GA', to: 'DUBAI', count: 12 },
                                    { from: 'NEW YORK, NY', to: 'ROTTERDAM', count: 8 },
                                    { from: 'LOS ANGELES', to: 'SHANGHAI', count: 6 },
                                ].map((route, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">{i + 1}</span>
                                        <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.from}</span>
                                        <span className="text-slate-400">→</span>
                                        <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.to}</span>
                                        <span className="ml-auto text-xs font-bold text-slate-600 dark:text-slate-300">{route.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">{t('topCustomers')}</h3>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { name: 'CLEAVER BROOKS CO.', quotes: 8 },
                                    { name: 'FAISAL TRADING', quotes: 5 },
                                    { name: 'SAKHAMET LTD', quotes: 4 },
                                ].map((customer, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
                                            <span className="text-xs text-slate-700 dark:text-slate-200">{customer.name}</span>
                                        </div>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{customer.quotes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5" />
                            <h3 className="font-bold">{t('aiRecommendations')}</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-blue-100">
                            <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('followUpRecommendation')}</li>
                            <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('volumeDiscountRecommendation')}</li>
                            <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('renewedQuotesRecommendation')}</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('lastUpdatedJustNow')}</p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                    >
                        {t('close')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const RateQuote = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { rateQuoteList, pagination, summary } = useSelector((s) => s.rateQuote);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');
    const authName = authUser?.username;
    const companyId = authUser?.companyId;

    // ── State ─────────────────────────────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('recent');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [fileUploadVisible, setFileUploadVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [showRateQuoteNote, setShowRateQuoteNote] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteRestoreModal, setShowDeleteRestoreModal] = useState(false);
    const [showDeletedRecords, setShowDeletedRecords] = useState(false);
    const [showAIInsights, setShowAIInsights] = useState(false);
    const [view, setView] = useState('table');
    const [isLoading, setIsLoading] = useState(false);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const _getList = async (page = 1) => {
        setIsLoading(true);
        setCurrentPage(page);
        const payload = {
            IPageNo: page,
            IPageRecords: recordsPerPage,
            FilterType: statusFilter,
            SearchKeyword: searchQuery,
            SortColumn: sortBy,
            SortDirection: sortDirection,
            compnayId: companyId,
        };
        try {
            if (showDeletedRecords) {
                payload.CompanyId = companyId; // extra field required by cancelled endpoint
                await dispatch(getCancelledRateQuoteList(payload));
            } else {
                await dispatch(getRateQuoteList(payload));
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (searchQuery.length === 0 || searchQuery.length >= 3) _getList(1);
    }, [searchQuery]);

    useEffect(() => {
        _getList(1);
    }, [sortBy, statusFilter, sortDirection, recordsPerPage, showDeletedRecords]);

    // In RateQuote.jsx - handleCopyQuote
    const handleCopyQuote = async (rateQuoteSno) => {
        // console.log("handleCopyQuote called with:", rateQuoteSno); // ADD THIS
        const confirmCopy = window.confirm("Are you sure you want to copy this Rate Quote?");
        if (!confirmCopy) return;

        console.log("confirmed, fetching data..."); // ADD THIS
        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(getRateQuoteByID(rateQuoteSno));
            // console.log("resultData:", resultData); // ADD THIS

            if (!resultData) {
                dispatch(showToast("Could not fetch quote data", "error"));
                return;
            }

            const CarrierDetails = (resultData?.CarrierDetails || []).map((carrier) => ({
                ...carrier,
                carrierDetailId: 0,
                rateCharges: (carrier.rateCharges || [])
                    .filter((c) => c.chargedescription?.trim())
                    .map((c) => ({ ...c, rateid: 0, ratequotesno: 0 })),
                rateQuoteContainer: (carrier.rateQuoteContainer || []).map((c) => ({ ...c, containerId: 0 })),
                ratequoteVessel: (carrier.ratequoteVessel || []).map((v) => ({ ...v, id: 0 })),
            }));

            const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');

            const payload = {
                ...resultData,
                rateQuoteSno: 0,
                effectiveDate: moment().format("YYYY-MM-DD"),
                valideDate: moment().add(30, "days").format("YYYY-MM-DD"),
                IsMultiCarrier: resultData?.IsMultiCarrier || false,
                CarrierDetails,
                cargoUnits: (resultData?.cargoUnits || []).map((u) => ({ ...u, id: 0 })),
                rateQuoteClauses: [],
                companyId: authUser?.companyId,
            };

            // createRateQuote is NOT a thunk — call it directly, no dispatch ✓
            const result = await createRateQuote(payload);
            // console.log("result", result);
            if (!result) {
                dispatch(showToast("Failed to copy rate quote", "error"));
                return;
            }

            dispatch(showToast("Rate Quote copied successfully!", "success", true));
            _getList(currentPage);

        } catch (e) {
            dispatch(showToast("Something went wrong", "error"));
            console.error("Error occurred while copying rate quote:", e);
        } finally {
            dispatch(callLoader(0));
        }

    };

    // ── Handlers ──────────────────────────────────────────────────────────────
    const _isDelete = async (remark) => {
        await deleteRateQuote({
            RateQuoteSno: selectedOrderId?.rateQuoteSno || 0,
            CancelBy: authName,
            RateQuoteCancelRemarks: remark,
        })
            .then(() => _getList(currentPage))
            .catch((e) => dispatch(showToast('error', e?.message || 'Failed to delete rate quote')))
            .finally(() => setShowDeleteModal(false));
    };

    const _isRestoreDeleted = async (remark) => {
        await reinstateRateQuoteList({
            RateQuoteSno: selectedOrderId?.rateQuoteSno || 0,
            ModifyBy: authName,
            RateQuoteReinstateRemarks: remark,
        })
            .then(() => _getList(currentPage))
            .catch((e) => dispatch(showToast('error', e?.message || 'Failed to restore rate quote')))
            .finally(() => setShowDeleteRestoreModal(false));
    };

    // ── KPI Cards ─────────────────────────────────────────────────────────────
    // conflict fix: v1 used bgGradient + CheckCircle2 for active; v2 uses accentKey + Activity.
    // Using v2 shape (accentKey) — AIInsightCard component must accept accentKey prop.
    const insightCards = useMemo(() => [
        { icon: FileText, title: t('totalQuotes'), value: summary?.total ?? 0, trend: t('fromLastMonthTrend'), trendUp: true, accentKey: 'blue' },
        { icon: Activity, title: t('active'), value: summary?.active ?? 0, trend: t('readyToConvert'), trendUp: true, accentKey: 'emerald' },
        { icon: AlertCircle, title: t('expired'), value: summary?.expire ?? 0, trend: t('needsRenewal'), trendUp: false, accentKey: 'red' },
        { icon: TrendingUp, title: t('conversionRate'), value: `${summary?.conversionRate ?? 0}%`, trend: t('improvementTrend'), trendUp: true, accentKey: 'amber' },
    ], [summary, t]);

    const statusOptions = [
        { value: 'ALL', label: t('all') },
        { value: 'ACTIVE', label: t('active') },
        { value: 'EXPIRED', label: t('expired') },
    ];

    const hasActiveFilters =
        searchQuery !== '' ||
        statusFilter !== 'ALL' ||
        sortBy !== 'recent' ||
        sortDirection !== 'desc';

    const resetFilters = () => {
        setSearchQuery('');
        setStatusFilter('ALL');
        setSortBy('recent');
        setSortDirection('desc');
    };

    const quoteSearchInputClass =
        'w-full pl-10 pr-9 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all';

    // ── Shared quote-card action handlers (passed as props) ───────────────────
    const makeHandlers = (quote) => ({
        onOpenFileUpload: () => { setSelectedOrderId(quote); setFileUploadVisible(true); },
        onOpenNotes: () => { setSelectedOrderId(quote); setShowRateQuoteNote(true); },
        onDelete: () => { setSelectedOrderId(quote); setShowDeleteModal(true); },
        onRestore: () => { setSelectedOrderId(quote); setShowDeleteRestoreModal(true); },
    });

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-full space-y-3 p-4">

            {/* ════ HEADER CARD ════════════════════════════════════════════════════ */}
            <div className="rounded-2xl overflow-hidden
                      border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900
                      shadow-sm dark:shadow-gray-900/40">

                {/* Top bar */}
                <div className="px-4 sm:px-5 py-4
                        flex flex-wrap items-center justify-between gap-3
                        border-b border-gray-100 dark:border-gray-800">

                    {/* Left — icon + title */}
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                            bg-blue-50 dark:bg-blue-900/40
                            ring-1 ring-blue-100 dark:ring-blue-800
                            flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                            <AnimatedTitle />
                            <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400 hidden sm:block truncate max-w-sm lg:max-w-lg">
                                {t('rateQuoteManagementDescription')}
                            </p>
                        </div>
                    </div>

                    {/* Right — action buttons + view toggle */}
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <button
                            onClick={() => navigate('/booking')}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-white dark:bg-gray-800
                         border border-gray-200 dark:border-gray-700
                         text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 hover:border-gray-300
                         dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                         text-[12.5px] font-semibold transition-colors"
                        >
                            <BookmarkPlus className="w-3.5 h-3.5" />
                           
                            <span>Export Shipment</span>
                        </button>

                        <button
                            onClick={() => navigate('/rate-quote/new')}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                         dark:bg-blue-500 dark:hover:bg-blue-600
                         text-white text-[12.5px] font-semibold
                         shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                         dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                         transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                          
                            <span>New Rate Quote</span>
                        </button>

                        <ViewToggle view={view} setView={setView} />
                    </div>
                </div>

                {/* KPI cards */}
                <div className="px-4 sm:px-5 pt-4 pb-3
                        grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
                    {insightCards.map((card) => (
                        <AIInsightCard key={card.title} {...card} />
                    ))}
                </div>

                {/* AI Insight Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="mx-4 sm:mx-5 mb-4 relative overflow-hidden
                     flex flex-col sm:flex-row items-start sm:items-center gap-3
                     rounded-xl px-4 py-3
                     bg-gradient-to-r from-violet-50 to-indigo-50
                     dark:from-violet-950/50 dark:to-indigo-950/50
                     border border-violet-100 dark:border-violet-900/60"
                >
                    <span aria-hidden
                        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl
                       bg-gradient-to-b from-violet-500 via-fuchsia-500 to-pink-500"
                    />
                    <div className="shrink-0 w-9 h-9 rounded-lg ml-1
                          bg-gradient-to-br from-violet-500 to-indigo-500
                          flex items-center justify-center
                          shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-400">
                            AI Insight
                        </div>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-700 dark:text-gray-300">
                            {t('rateQuoteAIBannerDescription')}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAIInsights(true)}
                        data-testid="view-ai-insights-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                       text-[12px] font-semibold
                       text-violet-700 dark:text-violet-300
                       bg-violet-100 dark:bg-violet-900/50
                       hover:bg-violet-200 dark:hover:bg-violet-900
                       ring-1 ring-violet-200 dark:ring-violet-800
                       transition-colors"
                    >
                        {t('viewDetails')} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                </motion.div>
            </div>
            {/* ════ END HEADER CARD ════ */}

            {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <SearchInputWithClear
                        placeholder={t('searchQuotePlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery('')}
                        iconLeft="left-4"
                        inputClassName={quoteSearchInputClass}
                    />

                    {/* Controls */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Status tabs */}
                        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                            {statusOptions.map(({ value, label }) => (
                                <button
                                    key={value}
                                    onClick={() => setStatusFilter(value)}
                                    className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${statusFilter === value
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* More filters dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="inline-flex items-center gap-2 px-3 py-2
                                   bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700
                                   text-gray-700 dark:text-gray-300 rounded-xl text-[12.5px] font-medium transition-all">
                                    <Filter className="w-4 h-4" />
                                    {['ALL', 'ACTIVE', 'EXPIRED'].includes(statusFilter) ? t('moreFilters') : statusFilter}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuPortal>
                                <DropdownMenuContent className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 min-w-[180px] z-50">
                                    {['RATEQUOTENUMBER', 'CUSTOMERNAME', 'CARRIERNAME', 'POL', 'POD', 'ORIGIN', 'DESTINATION'].map((s) => (
                                        <DropdownMenuItem
                                            key={s}
                                            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                            onSelect={() => setStatusFilter(s)}
                                        >
                                            {s}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenuPortal>
                        </DropdownMenu>

                        {/* Sort direction + column */}
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                {sortDirection === 'asc'
                                    ? <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                                    : <ArrowDownZA className="w-4 h-4 text-gray-500" />}
                            </button>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="recent">{t('mostRecent')}</option>
                                <option value="expiry">{t('expiryDate')}</option>
                                <option value="customer">{t('customerName')}</option>
                            </select>
                        </div>

                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-[12.5px] font-medium transition-all"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('reset')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Results summary + deleted toggle */}
                <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
                        {showDeletedRecords
                            ? t('showingDeletedQuotes', { count: rateQuoteList.length })
                            : t('showingOfQuotes', { shown: pagination?.currentRecord || 0, total: pagination?.totalRecord })}
                    </p>
                    <div className="flex items-center gap-3">
                        <span className="text-[12.5px] text-gray-500 dark:text-gray-400">{t('deletedQuotes')}</span>
                        <button
                            type="button"
                            onClick={() => setShowDeletedRecords((p) => !p)}
                            aria-pressed={showDeletedRecords}
                            className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors
                          focus:outline-none focus:ring-2 focus:ring-blue-400
                          ${showDeletedRecords ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
                               ${showDeletedRecords ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Content ─────────────────────────────────────────────────────────── */}
            <div className="p-1">
                {/* Empty state */}
                {!isLoading && rateQuoteList.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
                        <DollarSign className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">No rate quotes found</p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Create a new quote to get started</p>
                        <button
                            onClick={() => navigate('/rate-quote/new')}
                            className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
                        >
                            + {t('newRateQuote')}
                        </button>
                    </div>
                ) : view === 'card' ? (
                    /* Card grid */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {rateQuoteList.map((quote, i) => (
                            <QuoteCard
                                key={quote?.rateQuoteSno ?? i}
                                quote={quote}
                                basePath='rate-quote'
                                showDeletedRecords={showDeletedRecords}
                                navigate={navigate}
                                t={t}
                                {...makeHandlers(quote)}
                                onCopy={handleCopyQuote}
                            />
                        ))}
                    </div>
                ) : (
                    /* List view */
                    <div className="space-y-4">
                        <AnimatePresence>
                            {rateQuoteList.map((quote, i) => (
                                <QuoteCard
                                    key={quote?.rateQuoteSno ?? i}
                                    quote={quote}
                                    basePath='rate-quote'
                                    showDeletedRecords={showDeletedRecords}
                                    navigate={navigate}
                                    t={t}
                                    {...makeHandlers(quote)}
                                    onCopy={handleCopyQuote}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Pagination */}
                {!isLoading && !showDeletedRecords && (pagination?.totalPage ?? 0) > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-800
                          pl-2 pr-16 pt-4 pb-8 mb-4 mt-8
                          bg-gray-50 dark:bg-gray-800/50
                          flex items-center justify-between rounded-b-xl">
                        {/* Per-page selector */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <span>{t('show')}</span>
                            <select
                                value={recordsPerPage}
                                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-lg text-sm text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span>{t('entries')}</span>
                        </div>

                        {/* Page controls */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => currentPage > 1 && _getList(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                            >
                                {t('previous')}
                            </button>
                            <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-default">
                                {currentPage}
                            </button>
                            <button
                                onClick={() => pagination?.nextPage > 0 && _getList(pagination.nextPage)}
                                disabled={!pagination?.nextPage || pagination.nextPage === 0}
                                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                            >
                                {t('next')}
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('showingOfLoadPlans', { shown: pagination?.currentRecord || 0, total: pagination?.totalRecord || 0 })}
                        </p>
                    </div>
                )}
            </div>

            {/* ── Modals ──────────────────────────────────────────────────────────── */}
            <AnimatePresence>
                {showAIInsights && (
                    <AIInsightsModal
                        onClose={() => setShowAIInsights(false)}
                        insightCards={insightCards}
                    />
                )}
            </AnimatePresence>

            {fileUploadVisible && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
                        <FileUpload
                            orderId={selectedOrderId?.rateQuoteSno}
                            orderNum={selectedOrderId?.rateQuoteNumber}
                            type="rateQuote"
                        />
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRateQuoteNote && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
                        <RateQuoteNote
                            orderId={selectedOrderId?.rateQuoteSno}
                            orderNum={selectedOrderId?.rateQuoteNumber}
                            onClose={() => setShowRateQuoteNote(false)}
                        />
                    </div>
                </div>
            )}

            <RateQuoteCancelModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onDelete={_isDelete}
            />

            <RateQuoteCancelRestoreModal
                open={showDeleteRestoreModal}
                onClose={() => setShowDeleteRestoreModal(false)}
                title={t('restoreRateQuote')}
                description={t('restoreRateQuoteDescription')}
                confirmLabel={t('restore')}
                cancelLabel={t('cancel')}
                variant="warning"
                onDelete={_isRestoreDeleted}
            />
        </div>
    );
};

export default RateQuote;
// import React, { useState, useEffect, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
// import {
//     Search, Plus, FileText, Upload, Printer, Trash2, DollarSign,
//     Ship, MapPin, Package, Calendar, User, Filter, ChevronLeft,
//     ChevronRight, ChevronsLeft, ChevronsRight, Eye, MoreHorizontal,
//     Clock, CheckCircle2, AlertCircle, ArrowUpDown, ArrowDownZA, ArrowUpAZ, Mail,
//     TrendingUp,
//     Sparkles,
//     ArrowUpRight,
//     ArrowDownRight,
//     Globe,
//     XCircle,
//     Brain,
//     RotateCcw
// } from 'lucide-react';

// import { useDispatch, useSelector } from 'react-redux';
// import { useTranslation } from 'react-i18next';
// import { getRateQuoteList, updateRateQuote, deleteRateQuote, getCancelledRateQuoteList, reinstateRateQuoteList } from '@/redux/RateQuote/actions';
// import moment from 'moment';
// import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
// import FileUpload from '@/components/FileUpload/FileUpload';
// import RateQuoteNote from './RateQuote/RateQuoteNote';
// import RateQuoteCancelModal from '@/components/Modal/RateQuoteCancelModal';
// import { showToast } from '@/redux/Toast/actions';
// import RateQuoteCancelRestoreModal from '@/components/Modal/RateQuoteCancelRestoreModal';
// import PortBadge from './RateQuote/PortBadge';
// import DynamicButton from '@/components/ui/DynamicButton';

// // ───  Animated typewriter title ────────────────────────────────────────────────
// const TITLE = 'Rate Quote Management';
// function AnimatedTitle() {
//   return (
//     <motion.h1
//       initial={{ opacity: 0, y: 8, filter: 'blur(6px)' }}
//       animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
//       transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
//       className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
//                  text-gray-900 dark:text-white"
//     >
//       <motion.span
//         className="inline-block overflow-hidden whitespace-nowrap align-bottom"
//         initial={{ width: '0ch' }}
//         animate={{ width: ['0ch', `${TITLE.length}ch`, `${TITLE.length}ch`, '0ch'] }}
//         transition={{
//           duration: 4.8,
//           times: [0, 0.22, 0.9, 1],
//           ease: ['linear', 'linear', 'easeInOut'],
//           repeat: Infinity,
//         }}
//       >
//         {TITLE}
//       </motion.span>
//     </motion.h1>
//   );
// }

// // ─── View Toggle ──────────────────────────────────────────────────────────────
// function ViewToggle({ view, setView }) {
//   const base = 'w-9 h-9 flex items-center justify-center rounded-md transition-colors';
//   const active = 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
//   const inactive = 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100';

//   return (
//     <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
//                     bg-white dark:bg-gray-900 p-0.5">
//       <button onClick={() => setView('table')} title="List view"
//         className={`${base} ${view === 'table' ? active : inactive}`}>
//         <List className="w-4 h-4" />
//       </button>
//       <button onClick={() => setView('card')} title="Grid view"
//         className={`${base} ${view === 'card' ? active : inactive}`}>
//         <LayoutGrid className="w-4 h-4" />
//       </button>
//     </div>
//   );
// }

// // ─── Status Badge ─────────────────────────────────────────────────────────────
// const StatusBadge = ({ status }) => {
//   const styles = {
//     active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
//     expired: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
//     pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
//   };
//   const icons = {
//     active: <CheckCircle2 className="w-3.5 h-3.5" />,
//     expired: <AlertCircle className="w-3.5 h-3.5" />,
//     pending: <Clock className="w-3.5 h-3.5" />,
//   };
//   const s = status?.toLowerCase() || 'pending';
//   return (
//     <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[s] || styles.pending}`}>
//       {icons[s] || icons.pending}
//       {s.charAt(0).toUpperCase() + s.slice(1)}
//     </span>
//   );
// };

// // ─── Action Button ────────────────────────────────────────────────────────────
// const ActionButton = ({ icon: Icon, label, onClick, variant = 'default', id = null }) => {
//   const variants = {
//     default: 'bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-400',
//     primary: 'bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400',
//     warning: 'bg-amber-100 hover:bg-amber-200 text-amber-600 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400',
//     danger: 'bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400',
//   };

//   if (label === 'Print') {
//     return (
//       <a href={`/rate-quote/print/${btoa(id)}`} target="_blank" rel="noopener noreferrer"
//         className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`} title={label}>
//         <Icon className="w-4 h-4" />
//       </a>
//     );
//   }
//   return (
//     <button onClick={onClick}
//       className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`} title={label}>
//       <Icon className="w-4 h-4" />
//     </button>
//   );
// };

// // ─── Quote Card ───────────────────────────────────────────────────────────────
// // const QuoteCard = ({ quote, showDeletedRecords, navigate, t,
// //   onOpenFileUpload, onOpenNotes, onDelete, onRestore }) => (
// //   <motion.div
// //     initial={{ opacity: 0, y: 20 }}
// //     animate={{ opacity: 1, y: 0 }}
// //     exit={{ opacity: 0, y: -20 }}
// //     className="bg-white dark:bg-gray-900 rounded-xl
// //                border border-gray-200 dark:border-gray-800
// //                hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800
// //                transition-all duration-200 overflow-hidden group"
// //   >
// //     <div className="p-5">
// //       {/* Top row */}
// //       <div className="flex items-start justify-between mb-4">
// //         <div className="flex items-center gap-4">
// //           <button
// //             className="text-lg font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-2"
// //             onClick={() => !showDeletedRecords && navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)}
// //           >
// //             {quote?.rateQuoteNumber}
// //             {quote?.hasAlert && (
// //               <span className="flex h-2.5 w-2.5 relative">
// //                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
// //                 <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
// //               </span>
// //             )}
// //           </button>
// //           <StatusBadge status={quote?.loadingStatus || 'pending'} />
// //         </div>

// //         <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
// //           {showDeletedRecords ? (
// //             <ActionButton icon={RotateCcw} label={t('restore')} variant="warning" onClick={onRestore} />
// //           ) : (
// //             <>
// //               <ActionButton icon={Eye} label={t('viewDetails')} variant="primary"
// //                 onClick={() => navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)} />
// //               <ActionButton icon={Printer} label="Print" variant="warning" id={quote?.rateQuoteSno} />
// //               <ActionButton icon={Mail} label={t('mail')}
// //                 onClick={() => navigate(`/mail/compose?type=RateQuote&id=${quote?.rateQuoteSno}`)} />
// //               <ActionButton icon={Upload} label={t('upload')} onClick={onOpenFileUpload} />
// //               <ActionButton icon={FileText} label={t('documents')} onClick={onOpenNotes} />
// //               <ActionButton icon={Trash2} label={t('delete')} variant="danger" onClick={onDelete} />
// //             </>
// //           )}
// //         </div>
// //       </div>

// //       {/* Customer / Carrier */}
// //       <div className="grid grid-cols-2 gap-6 p-4 bg-white dark:bg-gray-900 rounded-lg mb-3">
// //         <div className="space-y-1">
// //           <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
// //             <User className="w-4 h-4" />{t('customer')}
// //           </div>
// //           <p className="text-base font-semibold text-slate-900 dark:text-white truncate">{quote?.customerName}</p>
// //         </div>
// //         <div className="space-y-1">
// //           <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
// //             <Ship className="w-4 h-4" />{t('carrier')}
// //           </div>
// //           <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
// //             {quote?.carrierName || '—'}
// //           </span>
// //         </div>
// //       </div>

// //       {/* Route */}
// //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4
// //                       bg-gray-50 dark:bg-gray-800/50 rounded-xl">
// //         <div>
// //           <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
// //             <Ship className="w-3.5 h-3.5" />{t('portOfLoading')}
// //           </div>
// //           <PortBadge port={quote?.portOfLoad || 'N/A'} code={quote?.portOfLoadCode} type="pol" />
// //         </div>
// //         <div className="hidden lg:flex items-center justify-center">
// //           <div className="w-full h-0.5 bg-gradient-to-r from-blue-400 to-emerald-400 relative">
// //             <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-400 rotate-45" />
// //           </div>
// //         </div>
// //         <div>
// //           <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
// //             <MapPin className="w-3.5 h-3.5" />{t('portOfDischarge')}
// //           </div>
// //           <PortBadge port={quote?.portOfDischarge} code={quote?.portOfDischargeCode} type="pod" />
// //         </div>
// //         <div>
// //           <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
// //             <Package className="w-3.5 h-3.5" />{t('container')}
// //           </div>
// //           <span className="inline-flex items-center px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-semibold">
// //             {quote?.moveType}
// //           </span>
// //         </div>
// //         <div>
// //           <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
// //             <Calendar className="w-3.5 h-3.5" />{t('expires')}
// //           </div>
// //           <span className={`text-sm font-semibold ${quote?.loadingStatus === 'expired'
// //             ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
// //             {quote?.valideDate ? moment(quote.valideDate).format('DD MMM YYYY') : 'N/A'}
// //           </span>
// //         </div>
// //       </div>

// //       {/* Footer */}
// //       <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800
// //                       flex items-center justify-between">
// //         <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
// //           <span className="flex items-center gap-1.5">
// //             <User className="w-4 h-4" />
// //             {t('createdBy')}{' '}
// //             <span className="font-medium text-slate-700 dark:text-slate-300">{quote?.quotedBy}</span>
// //           </span>
// //           {quote?.truckerName && (
// //             <span className="flex items-center gap-1.5">
// //               <Package className="w-4 h-4" />
// //               {t('trucker')}{' '}
// //               <span className="font-medium text-slate-700 dark:text-slate-300">{quote?.truckerName}</span>
// //             </span>
// //           )}
// //         </div>
// //         {!showDeletedRecords && (
// //           <button
// //             className="text-blue-600 hover:text-blue-700 dark:text-blue-400 text-sm font-medium flex items-center gap-1"
// //             onClick={() => navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)}
// //           >
// //             {t('viewFullDetails')} <ChevronRight className="w-4 h-4" />
// //           </button>
// //         )}
// //       </div>
// //     </div>
// //   </motion.div>
// // );

// // ─── AI Insights Modal ────────────────────────────────────────────────────────
// // const AIInsightsModal = ({ onClose = () => {}, insightCards = [] }) => {
// //   const { t } = useTranslation();
// //   const statThemes = [
// //     { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', sub: 'text-blue-600/70 dark:text-blue-400/70' },
// //     { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', sub: 'text-emerald-600/70 dark:text-emerald-400/70' },
// //     { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400', sub: 'text-red-600/70 dark:text-red-400/70' },
// //     { bg: 'bg-purple-50 dark:bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', sub: 'text-purple-600/70 dark:text-purple-400/70' },
// //   ];

// //   return (
// //     <div className="fixed inset-0 z-50 flex items-center justify-center">
// //       <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
// //       <motion.div
// //         initial={{ opacity: 0, scale: 0.95 }}
// //         animate={{ opacity: 1, scale: 1 }}
// //         exit={{ opacity: 0, scale: 0.95 }}
// //         className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl
// //                    w-full max-w-3xl max-h-[80vh] overflow-hidden"
// //       >
// //         {/* Header */}
// //         <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4">
// //           <div className="flex items-center justify-between">
// //             <div className="flex items-center gap-3">
// //               <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
// //                 <Brain className="w-5 h-5 text-white" />
// //               </div>
// //               <div>
// //                 <h2 className="text-xl font-bold text-white">{t('aiQuoteAnalytics')}</h2>
// //                 <p className="text-violet-100 text-sm">{t('aiQuoteAnalyticsDescription')}</p>
// //               </div>
// //             </div>
// //             <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
// //               <XCircle className="w-5 h-5 text-white" />
// //             </button>
// //           </div>
// //         </div>

// //         {/* Content */}
// //         <div className="p-6 overflow-y-auto max-h-[60vh]">
// //           <div className="grid grid-cols-4 gap-4 mb-6">
// //             {insightCards.map((card, i) => (
// //               <div key={card.title} className={`${statThemes[i]?.bg} rounded-xl p-4 text-center`}>
// //                 <p className={`text-2xl font-bold ${statThemes[i]?.text}`}>{card.value}</p>
// //                 <p className={`text-xs font-medium ${statThemes[i]?.sub}`}>{card.title}</p>
// //               </div>
// //             ))}
// //           </div>

// //           <div className="bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
// //             <div className="flex items-center gap-2 mb-3">
// //               <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
// //               <h3 className="font-bold text-slate-800 dark:text-white">{t('aiPredictions')}</h3>
// //             </div>
// //             <div className="space-y-3">
// //               {[
// //                 { icon: CheckCircle2, color: 'bg-emerald-500', title: t('highConversionPotential'), desc: t('highConversionPotentialDescription') },
// //                 { icon: AlertCircle, color: 'bg-amber-500', title: t('expiringSoon'), desc: t('expiringSoonDescription') },
// //                 { icon: TrendingUp, color: 'bg-blue-500', title: t('marketTrend'), desc: t('marketTrendDescription') },
// //               ].map(({ icon: Icon, color, title, desc }) => (
// //                 <div key={title} className="flex items-start gap-3">
// //                   <div className={`w-6 h-6 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
// //                     <Icon className="w-3.5 h-3.5 text-white" />
// //                   </div>
// //                   <div>
// //                     <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{title}</p>
// //                     <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
// //                   </div>
// //                 </div>
// //               ))}
// //             </div>
// //           </div>

// //           <div className="grid grid-cols-2 gap-6">
// //             <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
// //               <div className="flex items-center gap-2 mb-4">
// //                 <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
// //                 <h3 className="font-bold text-slate-800 dark:text-white">{t('topRoutes')}</h3>
// //               </div>
// //               <div className="space-y-3">
// //                 {[
// //                   { from: 'SAVANNAH, GA', to: 'DUBAI', count: 12 },
// //                   { from: 'NEW YORK, NY', to: 'ROTTERDAM', count: 8 },
// //                   { from: 'LOS ANGELES', to: 'SHANGHAI', count: 6 },
// //                 ].map((route, i) => (
// //                   <div key={i} className="flex items-center gap-2">
// //                     <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">{i + 1}</span>
// //                     <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.from}</span>
// //                     <span className="text-slate-400">→</span>
// //                     <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.to}</span>
// //                     <span className="ml-auto text-xs font-bold text-slate-600 dark:text-slate-300">{route.count}</span>
// //                   </div>
// //                 ))}
// //               </div>
// //             </div>
// //             <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
// //               <div className="flex items-center gap-2 mb-4">
// //                 <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
// //                 <h3 className="font-bold text-slate-800 dark:text-white">{t('topCustomers')}</h3>
// //               </div>
// //               <div className="space-y-3">
// //                 {[
// //                   { name: 'CLEAVER BROOKS CO.', quotes: 8 },
// //                   { name: 'FAISAL TRADING', quotes: 5 },
// //                   { name: 'SAKHAMET LTD', quotes: 4 },
// //                 ].map((customer, i) => (
// //                   <div key={i} className="flex items-center justify-between">
// //                     <div className="flex items-center gap-2">
// //                       <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
// //                       <span className="text-xs text-slate-700 dark:text-slate-200">{customer.name}</span>
// //                     </div>
// //                     <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{customer.quotes}</span>
// //                   </div>
// //                 ))}
// //               </div>
// //             </div>
// //           </div>

// //           <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
// //             <div className="flex items-center gap-2 mb-3">
// //               <Sparkles className="w-5 h-5" />
// //               <h3 className="font-bold">{t('aiRecommendations')}</h3>
// //             </div>
// //             <ul className="space-y-2 text-sm text-blue-100">
// //               <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('followUpRecommendation')}</li>
// //               <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('volumeDiscountRecommendation')}</li>
// //               <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('renewedQuotesRecommendation')}</li>
// //             </ul>
// //           </div>
// //         </div>

// //         <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
// //           <p className="text-xs text-slate-500 dark:text-slate-400">{t('lastUpdatedJustNow')}</p>
// //           <button onClick={onClose} className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors">
// //             {t('close')}
// //           </button>
// //         </div>
// //       </motion.div>
// //     </div>
// //   );
// // };

// // ─── Main Component ───────────────────────────────────────────────────────────
// const RateQuote = () => {
//     const navigate = useNavigate();
//     const [searchQuery, setSearchQuery] = useState('');
//     const [statusFilter, setStatusFilter] = useState('ALL');
//     const [sortBy, setSortBy] = useState('recent');
//     const [sortDirection, setSortDirection] = useState('desc');
//     const [currentPage, setCurrentPage] = useState(1);
//     const [recordsPerPage, setRecordsPerPage] = useState(10);
//     const [fileUploadVisible, setFileUploadVisible] = useState(false);
//     const [selectedOrderId, setSelectedOrderId] = useState(null);
//     const [showRateQuoteNote, setShowRateQuoteNote] = useState(false);
//     const [showDeleteModal, setShowDeleteModal] = useState(false);
//     const [showDeleteRestoreModal, setShowDeleteRestoreModal] = useState(false);
//     const [showDeletedRecords, setShowDeletedRecords] = useState(false);

//     const dispatch = useDispatch();
//     const { t } = useTranslation();
//     const { rateQuoteList, pagination, summary } = useSelector(state => state.rateQuote);
//     const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
//     const authName = authUser?.username;
//     const companyId = authUser?.companyId;

//     const statusOptions = [
//         { value: 'ALL', label: t('all') },
//         { value: 'ACTIVE', label: t('active') },
//         { value: 'EXPIRED', label: t('expired') }
//     ];
//     // console.log('rateQuoteList',rateQuoteList);
//     useEffect(() => {
//         if (searchQuery.length === 0 || searchQuery.length >= 3) {
//             _getList(1);
//         }
//     }, [searchQuery]);

//     useEffect(() => {
//         _getList(1);
//     }, [sortBy, statusFilter, sortDirection, recordsPerPage, showDeletedRecords]);

//     const handlePrevious = () => {
//         if (currentPage > 1) _getList(currentPage - 1);
//     };

//     const handleNext = () => {
//         if (pagination?.nextPage > 0) _getList(pagination.nextPage);
//     };

//     const _getList = async (page = 1) => {
//         setCurrentPage(page);
//         const payload = {
//             IPageNo: page,
//             IPageRecords: recordsPerPage,
//             FilterType: statusFilter,
//             SearchKeyword: searchQuery,
//             SortColumn: sortBy,
//             SortDirection: sortDirection
//         }
//         if (showDeletedRecords) {
//             payload.CompanyId = companyId;
//             await dispatch(getCancelledRateQuoteList(payload));
//         } else {
//             payload.compnayId = companyId;
//             await dispatch(getRateQuoteList(payload));
//         }
//     }

//     const StatusBadge = ({ status }) => {
//         const styles = {
//             active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
//             expired: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
//             pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'
//         };

//         const icons = {
//             active: <CheckCircle2 className="w-3.5 h-3.5" />,
//             expired: <AlertCircle className="w-3.5 h-3.5" />,
//             pending: <Clock className="w-3.5 h-3.5" />
//         };

//         return (
//             <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status]}`}>
//                 {icons[status]}
//                 {/* {status.charAt(0).toUpperCase() + status.slice(1)} */}
//             </span>
//         );
//     };

//     const PORT_COUNTRY_MAP = {
//         USORF: "US",
//         USCHI: "US",
//         INNSA: "IN",
//         INMUN: "IN",
//         CNSHA: "CN",
//         SGSIN: "SG",
//         AEDXB: "AE",
//         GBFXT: "GB"
//     };
//     // Country code to flag emoji mapping
//     // Country code to flag emoji mapping (ROBUST)
//     // const getCountryFlag = (portCode) => {
//     //     if (!portCode || typeof portCode !== "string") return "";

//     //     const code = portCode.trim().toUpperCase();

//     //     const countryCode =
//     //         PORT_COUNTRY_MAP[code] || code.substring(0, 2);

//     //     if (!/^[A-Z]{2}$/.test(countryCode)) return "";

//     //     return String.fromCodePoint(
//     //         ...[...countryCode].map(c => 127397 + c.charCodeAt(0))
//     //     );
//     // };

//     // const PortBadge = ({ port, code, type }) => {
//     //     const flag = getCountryFlag(code);

//     //     return (
//     //         <div className="flex items-center gap-2">

//     //             {/* FLAG */}
//     //             {flag && (
//     //                 <span
//     //                     className="text-xl leading-none select-none"
//     //                     style={{
//     //                         fontFamily:
//     //                             "Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji"
//     //                     }}
//     //                     title={code}
//     //                 >
//     //                     {flag}
//     //                 </span>
//     //             )}

//     //             {/* TEXT */}
//     //             <div className="flex flex-col">
//     //                 <span className="text-sm font-medium text-slate-900 dark:text-white">
//     //                     {port}
//     //                 </span>

//     //                 {code && (
//     //                     <span
//     //                         className={`text-xs ${type === "pol"
//     //                             ? "text-blue-600 dark:text-blue-400"
//     //                             : "text-emerald-600 dark:text-emerald-400"
//     //                             }`}
//     //                     >
//     //                         [{code}]
//     //                     </span>
//     //                 )}
//     //             </div>
//     //         </div>
//     //     );
//     // };

//     const ActionButton = ({ icon: Icon, label, onClick, variant = 'default', id = null }) => {
//         const variants = {
//             default: 'bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-400',
//             primary: 'bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400',
//             warning: 'bg-amber-100 hover:bg-amber-200 text-amber-600 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400',
//             danger: 'bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400'
//         };

//         return (
//             label === 'Print' ? (
//                 <a
//                     href={`/rate-quote/print/${btoa(id)}`} // Assuming the backend can handle base64 encoded IDs
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`}
//                     title={label}
//                 >
//                     <Icon className="w-4 h-4" />
//                 </a>
//             ) : (
//                 <button
//                     onClick={onClick}
//                     className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`}
//                     title={label}
//                 >
//                     <Icon className="w-4 h-4" />
//                 </button>
//             )
//         );
//     };

//     const _isDelete = async (data) => {
//         const payload = {
//             "RateQuoteSno": selectedOrderId?.rateQuoteSno || 0,
//             "CancelBy": authName,
//             "RateQuoteCancelRemarks": data
//         }
//         await deleteRateQuote(payload).then(result => {
//             _getList(currentPage);
//         }).catch(error => {
//             dispatch(showToast('error', error?.message || 'Failed to delete rate quote'));
//             console.error("Error deleting rate quote:", error);
//         }).finally(() => {
//             setShowDeleteModal(false);
//         });
//     }
//     //change
//     const _isRestoreDeleted = async (data) => {
//         const payload = {
//             "RateQuoteSno": selectedOrderId?.rateQuoteSno || 0,
//             "ModifyBy": authName,
//             "RateQuoteReinstateRemarks": data
//         }
//         await reinstateRateQuoteList(payload).then(result => {
//             _getList(currentPage);
//         }).catch(error => {
//             dispatch(showToast('error', error?.message || 'Failed to restore rate quote'));
//             console.error("Error restoring rate quote:", error);
//         }).finally(() => {
//             setShowDeleteRestoreModal(false);
//         });
//     }

//     //

//     const [showAIInsights, setShowAIInsights] = useState(false);

//     const insightCards = useMemo(() => {
//         return [
//             {
//                 icon: FileText,
//                 title: t('totalQuotes'),
//                 value: summary?.total ?? 0,
//                 trend: t('fromLastMonthTrend'),
//                 trendUp: true,
//                 bgGradient: "bg-gradient-to-br from-blue-500 to-blue-600",
//                 class: "text-blue-600 dark:text-blue-400",
//             },
//             {
//                 icon: CheckCircle2,
//                 title: t('active'),
//                 value: summary?.active,
//                 trend: t('readyToConvert'),
//                 trendUp: true,
//                 bgGradient: "bg-gradient-to-br from-emerald-500 to-emerald-600",
//                 class: "text-blue-600 dark:text-blue-400",
//             },
//             {
//                 icon: AlertCircle,
//                 title: t('expired'),
//                 value: summary?.expire,
//                 trend: t('needsRenewal'),
//                 trendUp: false,
//                 bgGradient: "bg-gradient-to-br from-red-500 to-rose-600",
//             },
//             {
//                 icon: TrendingUp,
//                 title: t('conversionRate'),
//                 value: `${summary?.conversionRate ?? summary?.expire ?? 0}%`,
//                 trend: t('improvementTrend'),
//                 trendUp: true,
//                 bgGradient: "bg-gradient-to-br from-purple-500 to-purple-600",
//             },
//         ];
//     }, [summary, t]);

//     console.log("summary", summary)
//     return (
//         <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.3 }}
//             className="p-6 space-y-6 min-h-screen"
//         >


//             <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-800 dark:via-blue-900 dark:to-indigo-950 relative overflow-hidden">
//                 {/* Background decorations */}
//                 <div className="absolute inset-0 overflow-hidden">
//                     <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full"></div>
//                     <div className="absolute top-20 -left-20 w-60 h-60 bg-white/5 rounded-full"></div>
//                     <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-white/5 rounded-full"></div>
//                 </div>

//                 <div className="relative z-10 px-6 py-6">
//                     {/* Title Row */}
//                     <div className="flex items-center justify-between mb-6">
//                         <div className="flex items-center gap-4">
//                             <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
//                                 <DollarSign className="w-6 h-6 text-white" />
//                             </div>
//                             <div>
//                                 <h1 className="text-2xl font-bold text-white">{t('rateQuoteManagement')}</h1>
//                                 <p className="text-blue-100 text-sm">{t('rateQuoteManagementDescription')}</p>
//                             </div>
//                         </div>
//                         <div className="flex items-center gap-3">
//                             <DynamicButton
//                                 label={t('newRateQuote')}
//                                 Icon={Plus}
//                                 gradient="from-white via-gray-50 to-gray-100"
//                                 hoverGradient="hover:from-gray-50 hover:to-gray-200"
//                                 textColor="text-blue-700"
//                                 onClick={() => navigate('/rate-quote/new')}
//                             />

//                             <DynamicButton
//                                 label={t('newBookingTitle')}
//                                 Icon={Plus}
//                                 onClick={() => navigate('/booking')}
//                             />

//                         </div>
//                     </div>

//                     {/* AI Analytics Cards */}
//                     <div className="grid grid-cols-4 gap-4">
//                         {insightCards.map((card, index) => (
//                             <AIInsightCard
//                                 key={index}
//                                 icon={card.icon}
//                                 title={card.title}
//                                 value={card.value}
//                                 trend={card.trend}
//                                 trendUp={card.trendUp}
//                                 bgGradient={card.bgGradient}
//                             />
//                         ))}
//                     </div>

//                     {/* AI Insight Banner */}
//                     <motion.div
//                         initial={{ opacity: 0, y: 10 }}
//                         animate={{ opacity: 1, y: 0 }}
//                         transition={{ delay: 0.3 }}
//                         className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4"
//                     >
//                         <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
//                             <Sparkles className="w-5 h-5 text-white" />
//                         </div>
//                         <div className="flex-1">
//                             <p className="text-white font-medium text-sm">{t('aiInsight')}</p>
//                             <p className="text-blue-100 text-xs">{t('rateQuoteAIBannerDescription')}</p>
//                         </div>
//                         <button
//                             onClick={() => setShowAIInsights(true)}
//                             className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
//                             data-testid="view-ai-insights-btn"
//                         >
//                             {t('viewDetails')}
//                         </button>
//                     </motion.div>
//                 </div>
//             </div>


//             {/* Filters Section */}
//             <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
//                 <div className="flex flex-col lg:flex-row gap-4">
//                     {/* Search Input */}
//                     <div className="flex-1 relative">
//                         <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
//                         <input
//                             type="text"
//                             placeholder={t('searchQuotePlaceholder')}
//                             value={searchQuery}
//                             onChange={(e) => setSearchQuery(e.target.value)}
//                             className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
//                         />
//                     </div>

//                     {/* Filter Controls */}
//                     <div className="flex flex-wrap items-center gap-3">
//                         <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-xl p-1">
//                             {statusOptions.map(({ value, label }) => (
//                                 <button
//                                     key={value}
//                                     onClick={() => setStatusFilter(value)}
//                                     className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${statusFilter === value
//                                         ? 'bg-blue-600 text-white shadow-sm'
//                                         : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
//                                         }`}
//                                 >
//                                     {label}
//                                 </button>
//                             ))}
//                         </div>
//                         <DropdownMenu>
//                             <DropdownMenuTrigger>
//                                 <button className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
//                                     <Filter className="w-4 h-4" />
//                                     {(['ALL', 'ACTIVE', 'EXPIRED'].includes(statusFilter) ? t('moreFilters') : statusFilter)}
//                                 </button>
//                             </DropdownMenuTrigger>
//                             <DropdownMenuPortal className="w-100">
//                                 <DropdownMenuContent className="bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 w-100">
//                                     {['RATEQUOTENUMBER', 'CUSTOMERNAME', 'CARRIERNAME', 'POL', 'POD', 'ORIGIN', 'DESTINATION'].map((status) => (
//                                         <DropdownMenuItem
//                                             className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
//                                             onSelect={() => setStatusFilter(status)}
//                                         >
//                                             {status}
//                                         </DropdownMenuItem>
//                                     ))}
//                                 </DropdownMenuContent>
//                             </DropdownMenuPortal>
//                         </DropdownMenu>
//                         <div className="flex items-center gap-2">
//                             <a onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}>
//                                 {
//                                     sortDirection === 'asc' ? <ArrowUpAZ className="w-4 h-4 text-slate-400" /> : <ArrowDownZA className="w-4 h-4 text-slate-400" />
//                                 }
//                             </a>
//                             <select
//                                 value={sortBy}
//                                 onChange={(e) => setSortBy(e.target.value)}
//                                 className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             >
//                                 <option value="recent">{t('mostRecent')}</option>
//                                 <option value="expiry">{t('expiryDate')}</option>
//                                 <option value="customer">{t('customerName')}</option>
//                             </select>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Results Summary */}
//                 <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
//                     <p className="text-sm text-slate-500 dark:text-slate-400">
//                         {showDeletedRecords ? (
//                             t('showingDeletedQuotes', { count: rateQuoteList.length })
//                         ) : (
//                             t('showingOfQuotes', { shown: rateQuoteList.length, total: pagination?.totalRecord })
//                         )}
//                     </p>
//                     <div className="flex items-center gap-3">
//                         <span className="text-sm text-slate-500 dark:text-slate-400">{t('deletedQuotes')}</span>
//                         <button
//                             type="button"
//                             onClick={() => setShowDeletedRecords(prev => !prev)}
//                             className={`relative inline-flex h-6 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${showDeletedRecords ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`}
//                             aria-pressed={showDeletedRecords}
//                         >
//                             <span
//                                 className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${showDeletedRecords ? 'translate-x-6' : 'translate-x-1'}`}
//                             />
//                         </button>
//                     </div>
//                 </div>
//             </div>

//             {/* Rate Quotes List */}
//             <div className="space-y-4">
//                 <AnimatePresence>
//                     {rateQuoteList?.map((quote, index) => (
//                         <motion.div
//                             key={quote?.rateQuoteSno ?? quote?.rowNum ?? index}
//                             initial={{ opacity: 0, y: 20 }}
//                             animate={{ opacity: 1, y: 0 }}
//                             exit={{ opacity: 0, y: -20 }}
//                             transition={{ delay: index * 0.05 }}
//                             className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-200 overflow-hidden group"
//                         >
//                             <div className="p-5">
//                                 {/* Top Row - Quote ID, Status, Actions */}
//                                 <div className="flex items-start justify-between mb-4">
//                                     <div className="flex items-center gap-4">
//                                         <button
//                                             className="text-lg font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-2"
//                                             onClick={() => !showDeletedRecords && navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)}
//                                         >
//                                             {quote?.rateQuoteNumber}
//                                             {quote?.hasAlert && (
//                                                 <span className="flex h-2.5 w-2.5 relative">
//                                                     <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
//                                                     <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
//                                                 </span>
//                                             )}
//                                         </button>
//                                         <StatusBadge status={quote?.loadingStatus || 'pending'} />
//                                     </div>

//                                     <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
//                                         {
//                                             showDeletedRecords ? (
//                                                 <>
//                                                     <ActionButton icon={RotateCcw} label={t('restore')} variant="warning" onClick={() => {
//                                                         setSelectedOrderId(quote);
//                                                         setShowDeleteRestoreModal(true);
//                                                     }} />
//                                                 </>
//                                             ) : (
//                                                 <>
//                                                     <ActionButton icon={Eye} label={t('viewDetails')} variant="primary"
//                                                         onClick={() => navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)}
//                                                     />
//                                                     <ActionButton icon={Printer} label={t('print')} variant="warning"
//                                                         onClick={() => navigate(`/rate-quote/print/${quote?.rateQuoteSno}`)}
//                                                         id={quote?.rateQuoteSno}
//                                                     />
//                                                     <ActionButton icon={Mail} label={t('mail')}
//                                                         onClick={() => navigate(`/mail/compose?type=RateQuote&id=${quote?.rateQuoteSno}`)}
//                                                     />
//                                                     <ActionButton icon={Upload} label={t('upload')}
//                                                         onClick={() => { setSelectedOrderId(quote); setFileUploadVisible(true); }}
//                                                     />
//                                                     <ActionButton icon={FileText} label={t('documents')}
//                                                         onClick={() => { setSelectedOrderId(quote); setShowRateQuoteNote(true); }}
//                                                     />
//                                                     <ActionButton icon={Trash2} label={t('delete')} variant="danger" onClick={() => {
//                                                         setSelectedOrderId(quote);
//                                                         setShowDeleteModal(true);
//                                                     }} />
//                                                 </>
//                                             )
//                                         }

//                                     </div>
//                                 </div>

//                                 {/* Customer Info */}
//                                 <div className="grid grid-cols-2 gap-6  p-4 bg-white dark:bg-slate-900">

//                                     {/* Customer */}
//                                     <div className="space-y-1">
//                                         <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
//                                             <User className="w-4 h-4" />
//                                             {t('customer')}
//                                         </div>

//                                         <p className="text-base font-semibold text-slate-900 dark:text-white truncate">
//                                             {quote?.customerName}
//                                         </p>
//                                     </div>

//                                     {/* Carrier */}
//                                     <div className="space-y-1">
//                                         <div className="flex items-center justify-start gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
//                                             <Ship className="w-4 h-4" />
//                                             {t('carrier')}
//                                         </div>

//                                         <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
//                                             {quote?.carrierName || ''}
//                                         </span>
//                                     </div>

//                                 </div>

//                                 {/* Route Info */}
//                                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
//                                     {/* POL */}
//                                     <div>
//                                         <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
//                                             <Ship className="w-3.5 h-3.5" />
//                                             {t('portOfLoading')}
//                                         </div>
//                                         <PortBadge
//                                             port={quote?.portOfLoad || "N/A"}
//                                             code={quote?.portOfLoadCode}
//                                             type="pol"
//                                         />
//                                     </div>

//                                     {/* Route Arrow for larger screens */}
//                                     <div className="hidden lg:flex items-center justify-center">
//                                         <div className="w-full h-0.5 bg-gradient-to-r from-blue-400 to-emerald-400 relative">
//                                             <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-400 rotate-45" />
//                                         </div>
//                                     </div>

//                                     {/* POD */}
//                                     <div>
//                                         <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
//                                             <MapPin className="w-3.5 h-3.5" />
//                                             {t('portOfDischarge')}
//                                         </div>
//                                         <PortBadge port={quote?.portOfDischarge} code={quote?.portOfDischargeCode} type="pod" />
//                                     </div>


//                                     {/* Container OR Carrier */}
//                                     <div>
//                                         <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
//                                             <Package className="w-3.5 h-3.5" />
//                                             {t('container')}
//                                         </div>
//                                         <span className="inline-flex items-center px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-semibold">
//                                             {quote?.moveType}
//                                         </span>

//                                     </div>

//                                     {/* Expiry Date */}
//                                     <div>
//                                         <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mb-1">
//                                             <Calendar className="w-3.5 h-3.5" />
//                                             {t('expires')}
//                                         </div>
//                                         <span className={`text-sm font-semibold ${quote?.loadingStatus === 'expired'
//                                             ? 'text-red-600 dark:text-red-400'
//                                             : 'text-slate-900 dark:text-white'
//                                             }`}>
//                                             {quote?.valideDate ? moment(quote.valideDate).format('DD MMM YYYY') : 'N/A'}
//                                         </span>
//                                     </div>
//                                 </div>

//                                 {/* Footer */}
//                                 <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
//                                     <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
//                                         <span className="flex items-center gap-1.5">
//                                             <User className="w-4 h-4" />
//                                             {t('createdBy')} <span className="font-medium text-slate-700 dark:text-slate-300">{quote?.quotedBy}</span>
//                                         </span>
//                                         {quote?.truckerName && (
//                                             <span className="flex items-center gap-1.5">
//                                                 <Package className="w-4 h-4" />
//                                                 {t('trucker')} <span className="font-medium text-slate-700 dark:text-slate-300">{quote?.truckerName}</span>
//                                             </span>
//                                         )}
//                                     </div>
//                                     {
//                                         !showDeletedRecords && (
//                                             <button
//                                                 className="text-blue-600 hover:text-blue-700 dark:text-blue-400 text-sm font-medium flex items-center gap-1"
//                                                 onClick={() => navigate(`/rate-quote/edit/${quote?.rateQuoteSno}`)}
//                                             >
//                                                 {t('viewFullDetails')} <ChevronRight className="w-4 h-4" />
//                                             </button>
//                                         )
//                                     }
//                                 </div>
//                             </div>
//                         </motion.div>
//                     ))}
//                 </AnimatePresence>

//                 {/* Pagination */}
//                 {!showDeletedRecords && (
//                     <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">

//                         <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
//                             <span>{t('show')}</span>
//                             <select
//                                 value={recordsPerPage}
//                                 onChange={(e) => {
//                                     setRecordsPerPage(Number(e.target.value));
//                                 }}
//                                 className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             >
//                                 <option value={5}>5</option>
//                                 <option value={10}>10</option>
//                                 <option value={25}>25</option>
//                                 <option value={50}>50</option>
//                             </select>
//                             <span>{t('entries')}</span>
//                         </div>
//                         <div className="flex items-center space-x-2">
//                             <button
//                                 onClick={handlePrevious}
//                                 disabled={currentPage === 1}
//                                 className="px-3 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
//                             >
//                                 {t('previous')}
//                             </button>

//                             <button className="px-3 py-1 text-sm bg-[#0072FF] text-white rounded">
//                                 {currentPage}
//                             </button>

//                             <button
//                                 onClick={handleNext}
//                                 disabled={pagination?.nextPage === 0}
//                                 className="px-3 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
//                             >
//                                 {t('next')}
//                             </button>
//                         </div>
//                         <div className="text-sm text-slate-600 dark:text-slate-400">
//                             {t('showingOfLoadPlans', { shown: pagination?.currentRecord || 0, total: pagination?.totalRecord || 0 })}
//                         </div>

//                     </div>
//                 )}
//             </div>


//             {
//                 fileUploadVisible &&
//                 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
//                     <div className="bg-white dark:bg-slate-800 w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
//                         <div>
//                             <FileUpload orderId={selectedOrderId?.rateQuoteSno} orderNum={selectedOrderId?.rateQuoteNumber} type={'rateQuote'} />
//                         </div>
//                         <div className="flex justify-end mt-6">

//                             <button
//                                 onClick={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
//                                 className="px-4 py-2 bg-gray-400 hover:bg-gray-500 rounded-lg"
//                             >
//                                 {t('close')}
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             }

//             {showRateQuoteNote &&
//                 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
//                     <div className="bg-white dark:bg-slate-800 w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
//                         <div>
//                             <RateQuoteNote
//                                 orderId={selectedOrderId?.rateQuoteSno}
//                                 orderNum={selectedOrderId?.rateQuoteNumber}
//                                 onClose={() => setShowRateQuoteNote(false)}
//                             />
//                         </div>
//                     </div>
//                 </div>
//             }

//             <AnimatePresence>
//                 {showAIInsights && <AIInsightsModal
//                     onClose={() => setShowAIInsights(false)}
//                     insightCards={insightCards}
//                 />}
//             </AnimatePresence>

//             <RateQuoteCancelModal
//                 open={showDeleteModal}
//                 onClose={() => setShowDeleteModal(false)}
//                 onDelete={(remark) => {
//                     _isDelete(remark);
//                 }}
//             />

//             <RateQuoteCancelRestoreModal
//                 open={showDeleteRestoreModal}
//                 onClose={() => setShowDeleteRestoreModal(false)}
//                 title={t('restoreRateQuote')}
//                 description={t('restoreRateQuoteDescription')}
//                 confirmLabel={t('restore')}
//                 cancelLabel={t('cancel')}
//                 variant="warning"
//                 onDelete={(remark) => {
//                     _isRestoreDeleted(remark);
//                 }}
//             />
//         </motion.div>
//     );
// };

// export default RateQuote;


// // AI Insight Card Component
// const AIInsightCard = ({ icon: Icon, title, value, trend, trendUp, bgGradient }) => (
//     <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         className={`${bgGradient} rounded-xl p-4 text-white relative overflow-hidden`}
//     >
//         <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-6 -mt-6"></div>
//         <div className="relative z-10">
//             <div className="flex items-center gap-2 mb-1">
//                 <Icon className="w-4 h-4 opacity-80" />
//                 <span className="text-xs font-medium opacity-90">{title}</span>
//             </div>
//             <div className="text-2xl font-bold">{value}</div>
//             {trend && (
//                 <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendUp ? 'text-green-200' : 'text-red-200'}`}>
//                     {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
//                     <span>{trend}</span>
//                 </div>
//             )}
//         </div>
//     </motion.div>
// );

// const AIInsightsModal = ({
//     onClose = () => { },
//     insightCards = [],
// }) => {

//     const statThemes = [
//         {
//             bg: "bg-blue-50 dark:bg-blue-500/10",
//             text: "text-blue-600 dark:text-blue-400",
//             subText: "text-blue-600/70 dark:text-blue-400/70",
//         },
//         {
//             bg: "bg-emerald-50 dark:bg-emerald-500/10",
//             text: "text-emerald-600 dark:text-emerald-400",
//             subText: "text-emerald-600/70 dark:text-emerald-400/70",
//         },
//         {
//             bg: "bg-red-50 dark:bg-red-500/10",
//             text: "text-red-600 dark:text-red-400",
//             subText: "text-red-600/70 dark:text-red-400/70",
//         },
//         {
//             bg: "bg-purple-50 dark:bg-purple-500/10",
//             text: "text-purple-600 dark:text-purple-400",
//             subText: "text-purple-600/70 dark:text-purple-400/70",
//         },
//     ];


//     return (
//         <div className="fixed inset-0 z-50 flex items-center justify-center">
//             <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
//             <motion.div
//                 initial={{ opacity: 0, scale: 0.95 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0.95 }}
//                 className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
//             >
//                 {/* Header */}
//                 <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
//                     <div className="flex items-center justify-between">
//                         <div className="flex items-center gap-3">
//                             <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
//                                 <Brain className="w-5 h-5 text-white" />
//                             </div>
//                             <div>
//                                 <h2 className="text-xl font-bold text-white">{t('aiQuoteAnalytics')}</h2>
//                                 <p className="text-purple-100 text-sm">{t('aiQuoteAnalyticsDescription')}</p>
//                             </div>
//                         </div>
//                         <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
//                             <XCircle className="w-5 h-5 text-white" />
//                         </button>
//                     </div>
//                 </div>

//                 {/* Content */}
//                 <div className="p-6 overflow-y-auto max-h-[60vh]">
//                     {/* Summary Stats */}
//                     <div className="grid grid-cols-4 gap-4 mb-6">

//                         {insightCards.map((card, index) => (
//                             <div
//                                 key={card.title}
//                                 className={`${statThemes[index].bg} rounded-xl p-4 text-center`}
//                             >
//                                 <p className={`text-2xl font-bold ${statThemes[index].text}`}>
//                                     {card.value}
//                                 </p>
//                                 <p className={`text-xs font-medium ${statThemes[index].subText}`}>
//                                     {card.title}
//                                 </p>
//                             </div>
//                         ))}

//                     </div>

//                     {/* AI Predictions */}
//                     <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
//                         <div className="flex items-center gap-2 mb-3">
//                             <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
//                             <h3 className="font-bold text-slate-800 dark:text-white">{t('aiPredictions')}</h3>
//                         </div>
//                         <div className="space-y-3">
//                             <div className="flex items-start gap-3">
//                                 <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
//                                     <CheckCircle2 className="w-3.5 h-3.5 text-white" />
//                                 </div>
//                                 <div>
//                                     <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('highConversionPotential')}</p>
//                                     <p className="text-xs text-slate-500 dark:text-slate-400">{t('highConversionPotentialDescription')}</p>
//                                 </div>
//                             </div>
//                             <div className="flex items-start gap-3">
//                                 <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
//                                     <AlertCircle className="w-3.5 h-3.5 text-white" />
//                                 </div>
//                                 <div>
//                                     <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('expiringSoon')}</p>
//                                     <p className="text-xs text-slate-500 dark:text-slate-400">{t('expiringSoonDescription')}</p>
//                                 </div>
//                             </div>
//                             <div className="flex items-start gap-3">
//                                 <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
//                                     <TrendingUp className="w-3.5 h-3.5 text-white" />
//                                 </div>
//                                 <div>
//                                     <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('marketTrend')}</p>
//                                     <p className="text-xs text-slate-500 dark:text-slate-400">{t('marketTrendDescription')}</p>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>

//                     {/* Top Routes & Customers */}
//                     <div className="grid grid-cols-2 gap-6">
//                         <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
//                             <div className="flex items-center gap-2 mb-4">
//                                 <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
//                                 <h3 className="font-bold text-slate-800 dark:text-white">{t('topRoutes')}</h3>
//                             </div>
//                             <div className="space-y-3">
//                                 {[
//                                     { from: 'SAVANNAH, GA', to: 'DUBAI', count: 12 },
//                                     { from: 'NEW YORK, NY', to: 'ROTTERDAM', count: 8 },
//                                     { from: 'LOS ANGELES', to: 'SHANGHAI', count: 6 },
//                                 ].map((route, i) => (
//                                     <div key={i} className="flex items-center gap-2">
//                                         <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">{i + 1}</span>
//                                         <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.from}</span>
//                                         <span className="text-slate-400">→</span>
//                                         <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.to}</span>
//                                         <span className="ml-auto text-xs font-bold text-slate-600 dark:text-slate-300">{route.count}</span>
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>

//                         <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
//                             <div className="flex items-center gap-2 mb-4">
//                                 <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
//                                 <h3 className="font-bold text-slate-800 dark:text-white">{t('topCustomers')}</h3>
//                             </div>
//                             <div className="space-y-3">
//                                 {[
//                                     { name: 'CLEAVER BROOKS CO.', quotes: 8 },
//                                     { name: 'FAISAL TRADING', quotes: 5 },
//                                     { name: 'SAKHAMET LTD', quotes: 4 },
//                                 ].map((customer, i) => (
//                                     <div key={i} className="flex items-center justify-between">
//                                         <div className="flex items-center gap-2">
//                                             <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
//                                             <span className="text-xs text-slate-700 dark:text-slate-200">{customer.name}</span>
//                                         </div>
//                                         <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{customer.quotes}</span>
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>
//                     </div>

//                     {/* Recommendations */}
//                     <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
//                         <div className="flex items-center gap-2 mb-3">
//                             <Sparkles className="w-5 h-5" />
//                             <h3 className="font-bold">{t('aiRecommendations')}</h3>
//                         </div>
//                         <ul className="space-y-2 text-sm text-blue-100">
//                             <li className="flex items-start gap-2">
//                                 <span className="text-blue-300">•</span>
//                                 {t('followUpRecommendation')}
//                             </li>
//                             <li className="flex items-start gap-2">
//                                 <span className="text-blue-300">•</span>
//                                 {t('volumeDiscountRecommendation')}
//                             </li>
//                             <li className="flex items-start gap-2">
//                                 <span className="text-blue-300">•</span>
//                                 {t('renewedQuotesRecommendation')}
//                             </li>
//                         </ul>
//                     </div>
//                 </div>

//                 {/* Footer */}
//                 <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
//                     <div className="flex items-center justify-between">
//                         <p className="text-xs text-slate-500 dark:text-slate-400">{t('lastUpdatedJustNow')}</p>
//                         <button onClick={onClose} className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors">
//                             {t('close')}
//                         </button>
//                     </div>
//                 </div>
//             </motion.div>
//         </div>
//     );
// }